/**
 * Clarix — Supplier Registry Page
 * Route: /suppliers
 *
 * Features:
 *  - Supplier list table with real-time search + category/country filters
 *  - Add Supplier modal (full form)
 *  - Edit / Archive actions per row
 *  - Click row → /suppliers/:id (SupplierProfile page)
 *
 * Add to App.js:
 *   import Suppliers       from './pages/Suppliers';
 *   import SupplierProfile from './pages/SupplierProfile';
 *   <Route path="/suppliers"     element={<Suppliers />} />
 *   <Route path="/suppliers/:id" element={<SupplierProfile />} />
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────

const V = {
  ink:      '#0c1830',
  ink2:     '#2a3956',
  ink3:     '#5a6680',
  ink4:     '#8a93a6',
  paper:    '#fbfaf6',
  paper2:   '#f3f1ea',
  paper3:   '#e6e2d5',
  line:     '#d8d3c2',
  gold:     '#c8a64b',
  good:     '#2a6f4f',
  goodSoft: '#e8f0eb',
  bad:      '#9c2b1a',
  badSoft:  '#f4e8e5',
  warn:     '#a8731a',
  warnSoft: '#f5ecd9',
  serif:    "'Source Serif 4', Georgia, serif",
  sans:     "'Inter', -apple-system, system-ui, sans-serif",
  mono:     "'JetBrains Mono', ui-monospace, monospace",
};

// ─── FONT LOADER ──────────────────────────────────────────────────────────────

function FontLoader() {
  useEffect(() => {
    if (document.getElementById('clarix-fonts')) return;
    const link = document.createElement('link');
    link.id   = 'clarix-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,300;8..60,400;8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel  = 'stylesheet';
    document.head.appendChild(link);
  }, []);
  return null;
}

// ─── CATEGORY CONFIG ──────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'steel',     label: 'Steel',     color: '#5b21b6', bg: '#ede9fe' },
  { value: 'bunkers',   label: 'Bunkers',   color: '#1a4d8f', bg: '#dbeafe' },
  { value: 'electrical',label: 'Electrical',color: '#a8731a', bg: '#f5ecd9' },
  { value: 'civil',     label: 'Civil',     color: '#2a6f4f', bg: '#e8f0eb' },
  { value: 'equipment', label: 'Equipment', color: '#7c2d12', bg: '#fef3c7' },
  { value: 'services',  label: 'Services',  color: '#374151', bg: '#f3f4f6' },
  { value: 'other',     label: 'Other',     color: '#5a6680', bg: '#e6e2d5' },
];

function CategoryBadge({ category }) {
  const cfg = CATEGORIES.find(c => c.value === category) || CATEGORIES[CATEGORIES.length - 1];
  return (
    <span style={{
      fontFamily: V.mono, fontSize: 9, fontWeight: 500, letterSpacing: 1,
      padding: '2px 8px', borderRadius: 3, textTransform: 'uppercase',
      color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function StarRating({ value }) {
  if (value == null) return <span style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>No ratings</span>;
  const stars = Math.round(value);
  return (
    <span style={{ fontFamily: V.mono, fontSize: 12, color: V.gold, letterSpacing: 1 }}>
      {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
      <span style={{ color: V.ink3, marginLeft: 6 }}>{value.toFixed(1)}</span>
    </span>
  );
}

// ─── ADD / EDIT SUPPLIER MODAL ────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '', category: 'other', country: '',
  contact_name: '', contact_email: '', contact_phone: '', notes: '',
};

function SupplierModal({ supplier, onClose, onSaved }) {
  const [form, setForm]     = useState(supplier ? {
    name:          supplier.name          || '',
    category:      supplier.category      || 'other',
    country:       supplier.country       || '',
    contact_name:  supplier.contact_name  || '',
    contact_email: supplier.contact_email || '',
    contact_phone: supplier.contact_phone || '',
    notes:         supplier.notes         || '',
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Supplier name is required.'); return; }
    setSaving(true); setError(null);
    try {
      if (supplier) {
        await api.put(`/api/suppliers/${supplier.id}`, form);
      } else {
        await api.post('/api/suppliers', form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>
        <div style={{ ...s.modalHeader, borderTop: `3px solid ${V.gold}` }}>
          <div>
            <div style={s.modalTitle}>{supplier ? 'Edit Supplier' : 'Register New Supplier'}</div>
            <div style={s.modalSub}>All fields except name are optional</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.modalBody}>
          {/* Row 1: Name + Category */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14 }}>
            <div style={s.field}>
              <label style={s.fieldLabel}>Company name <span style={{ color: V.gold }}>*</span></label>
              <input
                style={s.input} autoFocus
                placeholder="e.g. Stella Maris Co. IKE"
                value={form.name} onChange={e => setF('name', e.target.value)}
              />
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Category</label>
              <select style={s.input} value={form.category} onChange={e => setF('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Country */}
          <div style={s.field}>
            <label style={s.fieldLabel}>Country</label>
            <input style={s.input} placeholder="e.g. Greece" value={form.country} onChange={e => setF('country', e.target.value)} />
          </div>

          {/* Row 3: Contact */}
          <div style={{ ...s.sectionDivider }}>Contact Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={s.field}>
              <label style={s.fieldLabel}>Contact name</label>
              <input style={s.input} placeholder="Primary contact" value={form.contact_name} onChange={e => setF('contact_name', e.target.value)} />
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Email</label>
              <input style={s.input} type="email" placeholder="contact@supplier.com" value={form.contact_email} onChange={e => setF('contact_email', e.target.value)} />
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Phone</label>
              <input style={s.input} placeholder="+30 210 000 0000" value={form.contact_phone} onChange={e => setF('contact_phone', e.target.value)} />
            </div>
          </div>

          {/* Notes */}
          <div style={s.field}>
            <label style={s.fieldLabel}>Internal notes <span style={{ color: V.ink4, fontWeight: 400 }}>(optional)</span></label>
            <textarea
              style={{ ...s.input, resize: 'vertical', minHeight: 60 }}
              placeholder="Reliability notes, preferred contact hours, etc."
              value={form.notes} onChange={e => setF('notes', e.target.value)}
            />
          </div>

          {error && (
            <div style={s.errorBox}>{error}</div>
          )}
        </div>

        <div style={s.modalFooter}>
          <button style={s.btnGhost} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...s.btnGold, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : supplier ? 'Save changes' : 'Register supplier'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TOP NAV ──────────────────────────────────────────────────────────────────

function TopNav({ user, logout, navigate }) {
  return (
    <div style={s.nav}>
      <div style={s.wordmark} onClick={() => navigate('/')} role="button">
        <span style={{ color: V.gold }}>V</span>
        <span style={{ color: V.ink }}>erdict</span>
      </div>
      <div style={s.wordmarkDivider}></div>
      <div style={s.wordmarkTag}>Supplier Registry</div>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={s.navItem} onClick={() => navigate('/')}>Negotiations</span>
        <span style={{ ...s.navItem, color: V.gold, borderBottom: `2px solid ${V.gold}`, paddingBottom: 2 }}>Suppliers</span>
        {user && <span style={{ fontSize: 12, color: V.ink4, fontFamily: V.sans }}>{user.email}</span>}
        {logout && <button style={s.navSignOut} onClick={logout}>Sign out</button>}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function Suppliers() {
  const navigate        = useNavigate();
  const { user, logout } = useAuth();

  const [suppliers, setSuppliers]   = useState([]);
  const [filters, setFilters]       = useState({ categories: [], countries: [] });
  const [loading, setLoading]       = useState(true);

  // Filter state
  const [search, setSearch]         = useState('');
  const [category, setCategory]     = useState('');
  const [country, setCountry]       = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Modal state
  const [addOpen, setAddOpen]       = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [archiving, setArchiving]   = useState(null);

  const debounceRef = useRef(null);

  const load = useCallback((overrides = {}) => {
    setLoading(true);
    const s        = overrides.search   !== undefined ? overrides.search   : search;
    const cat      = overrides.category !== undefined ? overrides.category : category;
    const cou      = overrides.country  !== undefined ? overrides.country  : country;
    const archived = overrides.archived !== undefined ? overrides.archived : showArchived;

    const params = new URLSearchParams();
    if (s)        params.set('search',    s);
    if (cat)      params.set('category',  cat);
    if (cou)      params.set('country',   cou);
    if (!archived) params.set('is_active', '1');

    api.get(`/api/suppliers?${params}`)
      .then(res => {
        setSuppliers(res.data.suppliers || []);
        setFilters(res.data.filters || { categories: [], countries: [] });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, category, country, showArchived]);

  // Initial load
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Debounced search — fires 350ms after user stops typing
  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load({ search: val });
    }, 350);
  };

  // Filter dropdowns fire immediately (no debounce needed)
  const handleCategoryChange = (val) => { setCategory(val); load({ category: val }); };
  const handleCountryChange  = (val) => { setCountry(val);  load({ country: val });  };

  const handleArchive = async (sup) => {
    if (!window.confirm(`Archive "${sup.name}"? They can be restored later.`)) return;
    setArchiving(sup.id);
    try {
      await api.delete(`/api/suppliers/${sup.id}`);
      load();
    } catch (e) {
      alert('Archive failed.');
    } finally { setArchiving(null); }
  };

  // Derived
  const activeCount   = suppliers.filter(s => s.is_active).length;
  const archivedCount = suppliers.filter(s => !s.is_active).length;

  return (
    <div style={s.wrap}>
      <FontLoader />

      {/* Util bar */}
      <div style={s.utilBar}>
        <div style={s.utilDot}></div>
        <span style={s.utilLabel}>SUPPLIER REGISTRY</span>
        <span style={{ fontSize: 11, color: 'rgba(251,250,246,0.45)', fontFamily: V.sans }}>
          {activeCount} active supplier{activeCount !== 1 ? 's' : ''}
        </span>
      </div>

      <TopNav user={user} logout={logout} navigate={navigate} />

      {/* Page header */}
      <div style={s.pageHeader}>
        <div>
          <div style={{ fontFamily: V.serif, fontSize: 28, fontWeight: 400, color: V.ink, letterSpacing: -0.5 }}>
            Supplier Registry
          </div>
          <div style={{ fontSize: 13, color: V.ink3, marginTop: 4, fontFamily: V.sans }}>
            Company-wide supplier profiles, linked to all their documents and pricing history
          </div>
        </div>
        <button style={s.btnGold} onClick={() => setAddOpen(true)}>
          + Add Supplier
        </button>
      </div>

      {/* Filter bar */}
      <div style={s.filterBar}>
        <input
          style={{ ...s.searchInput }}
          placeholder="Search suppliers…"
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
        />
        <select style={s.filterSelect} value={category} onChange={e => handleCategoryChange(e.target.value)}>
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select style={s.filterSelect} value={country} onChange={e => handleCountryChange(e.target.value)}>
          <option value="">All countries</option>
          {filters.countries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: V.ink3, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: V.sans }}>
          <input type="checkbox" checked={showArchived} onChange={e => { setShowArchived(e.target.checked); load({ archived: e.target.checked }); }} />
          Show archived
        </label>
        {(search || category || country) && (
          <button style={s.clearBtn} onClick={() => {
            setSearch(''); setCategory(''); setCountry('');
            clearTimeout(debounceRef.current);
            load({ search: '', category: '', country: '' });
          }}>
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div style={s.tableWrap}>
        {loading ? (
          <div style={s.emptyMsg}>Loading suppliers…</div>
        ) : suppliers.length === 0 ? (
          <div style={s.emptyMsg}>
            {search || category || country
              ? 'No suppliers match your filters.'
              : 'No suppliers registered yet. Add your first supplier to get started.'
            }
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr style={s.thead}>
                <th style={{ ...s.th, width: '26%' }}>Company</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Country</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Documents</th>
                <th style={s.th}>Last Activity</th>
                <th style={s.th}>Rating</th>
                <th style={s.th}>Status</th>
                <th style={{ ...s.th, width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map(sup => (
                <tr
                  key={sup.id}
                  style={s.row}
                  onClick={() => navigate(`/suppliers/${sup.id}`)}
                  onMouseEnter={e => { e.currentTarget.style.background = V.paper2; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={s.td}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: V.ink, fontFamily: V.sans, marginBottom: 2 }}>
                      {sup.name}
                    </div>
                    {sup.contact_name && (
                      <div style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>{sup.contact_name}</div>
                    )}
                  </td>
                  <td style={s.td}><CategoryBadge category={sup.category} /></td>
                  <td style={s.td}>
                    <span style={{ fontSize: 12, color: V.ink3, fontFamily: V.sans }}>{sup.country || '—'}</span>
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    <span style={{ fontFamily: V.mono, fontSize: 13, color: V.ink2 }}>
                      {sup.document_count || 0}
                    </span>
                  </td>
                  <td style={s.td}>
                    <span style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>
                      {sup.last_activity
                        ? new Date(sup.last_activity).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'No activity'}
                    </span>
                  </td>
                  <td style={s.td}><StarRating value={sup.rating} /></td>
                  <td style={s.td}>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 3,
                      fontFamily: V.mono, letterSpacing: 0.5,
                      color: sup.is_active ? V.good : V.ink4,
                      background: sup.is_active ? V.goodSoft : V.paper3,
                    }}>
                      {sup.is_active ? 'ACTIVE' : 'ARCHIVED'}
                    </span>
                  </td>
                  <td style={s.td} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        style={s.actionBtn}
                        title="Edit"
                        onClick={() => setEditTarget(sup)}
                      >
                        ✎
                      </button>
                      {sup.is_active && (
                        <button
                          style={{ ...s.actionBtn, opacity: archiving === sup.id ? 0.5 : 1 }}
                          title="Archive"
                          onClick={() => handleArchive(sup)}
                          disabled={archiving === sup.id}
                        >
                          ⊘
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer count */}
      {!loading && suppliers.length > 0 && (
        <div style={{ padding: '12px 48px', fontSize: 11, color: V.ink4, fontFamily: V.sans, borderTop: `1px solid ${V.line}` }}>
          Showing {suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}
          {archivedCount > 0 && !showArchived && ` · ${archivedCount} archived (hidden)`}
        </div>
      )}

      {/* Modals */}
      {addOpen && (
        <SupplierModal
          supplier={null}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}
      {editTarget && (
        <SupplierModal
          supplier={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────

const s = {
  wrap:        { minHeight: '100vh', background: V.paper2, fontFamily: V.sans, color: V.ink },

  utilBar:     { background: V.ink, height: 32, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 12 },
  utilDot:     { width: 6, height: 6, borderRadius: '50%', background: V.gold, flexShrink: 0 },
  utilLabel:   { fontSize: 9, fontWeight: 700, letterSpacing: 2, color: V.gold, textTransform: 'uppercase' },

  nav:         { background: V.paper, borderBottom: `1px solid ${V.line}`, height: 60, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 0 },
  wordmark:    { display: 'flex', alignItems: 'baseline', fontFamily: V.serif, fontSize: 24, fontWeight: 600, letterSpacing: -0.5, lineHeight: 1, cursor: 'pointer' },
  wordmarkDivider: { width: 1, height: 18, background: V.line, margin: '0 12px' },
  wordmarkTag: { fontSize: 10, fontWeight: 500, letterSpacing: 2.5, textTransform: 'uppercase', color: V.ink3 },
  navItem:     { fontSize: 13, fontWeight: 500, cursor: 'pointer', color: V.ink3, padding: '0 16px', letterSpacing: 0.1 },
  navSignOut:  { background: 'none', border: 'none', fontSize: 12, color: V.ink3, cursor: 'pointer', padding: 0, fontFamily: V.sans },

  pageHeader:  { padding: '28px 48px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', borderBottom: `1px solid ${V.line}`, background: V.paper },

  filterBar:   { padding: '14px 48px', display: 'flex', gap: 10, alignItems: 'center', background: V.paper, borderBottom: `1px solid ${V.line}`, flexWrap: 'wrap' },
  searchInput: { background: V.paper2, border: `1px solid ${V.line}`, borderRadius: 4, padding: '8px 12px', fontSize: 13, color: V.ink, fontFamily: V.sans, outline: 'none', width: 220 },
  filterSelect:{ background: V.paper2, border: `1px solid ${V.line}`, borderRadius: 4, padding: '8px 10px', fontSize: 12, color: V.ink, fontFamily: V.sans, outline: 'none', cursor: 'pointer' },
  clearBtn:    { background: 'none', border: `1px solid ${V.line}`, borderRadius: 4, padding: '6px 12px', fontSize: 11, color: V.ink3, cursor: 'pointer', fontFamily: V.sans },

  tableWrap:   { overflowX: 'auto', margin: '0 48px 32px', border: `0.5px solid ${V.line}`, borderRadius: 6, background: V.paper },
  table:       { width: '100%', borderCollapse: 'collapse' },
  thead:       { background: V.paper2 },
  th:          { fontSize: 9, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink4, padding: '12px 16px', textAlign: 'left', borderBottom: `1px solid ${V.line}`, fontFamily: V.sans, whiteSpace: 'nowrap' },
  row:         { cursor: 'pointer', transition: 'background 0.1s', borderBottom: `0.5px solid ${V.line}` },
  td:          { padding: '14px 16px', verticalAlign: 'middle' },

  actionBtn:   { background: 'none', border: `0.5px solid ${V.line}`, borderRadius: 3, padding: '3px 8px', fontSize: 12, color: V.ink3, cursor: 'pointer', fontFamily: V.mono },

  emptyMsg:    { padding: '60px 24px', textAlign: 'center', fontSize: 13, color: V.ink4, fontFamily: V.sans },

  // Modal
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(12,24,48,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal:       { background: V.paper, borderRadius: 6, width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 32px 64px rgba(12,24,48,0.22)' },
  modalHeader: { padding: '22px 26px 18px', borderBottom: `1px solid ${V.line}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  modalTitle:  { fontFamily: V.serif, fontSize: 20, fontWeight: 500, color: V.ink },
  modalSub:    { fontSize: 12, color: V.ink4, marginTop: 3, fontFamily: V.sans },
  closeBtn:    { background: 'none', border: 'none', color: V.ink4, fontSize: 16, cursor: 'pointer', padding: 4 },
  modalBody:   { padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 16, background: V.paper2, maxHeight: '70vh', overflowY: 'auto' },
  modalFooter: { padding: '16px 26px', borderTop: `1px solid ${V.line}`, display: 'flex', justifyContent: 'flex-end', gap: 10, background: V.paper },

  sectionDivider: { fontSize: 9, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: V.ink4, paddingBottom: 4, borderBottom: `1px solid ${V.line}`, fontFamily: V.sans },

  field:       { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel:  { fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink3, fontFamily: V.sans },
  input:       { background: V.paper, border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 12px', fontSize: 13, color: V.ink, fontFamily: V.sans, outline: 'none', width: '100%', boxSizing: 'border-box' },
  errorBox:    { background: '#f4e8e5', border: `1px solid ${V.bad}`, borderRadius: 4, padding: '10px 14px', fontSize: 12, color: V.bad },

  btnGold:     { background: V.gold, color: V.ink, border: 'none', borderRadius: 4, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: V.sans, letterSpacing: 0.1, whiteSpace: 'nowrap' },
  btnGhost:    { background: 'transparent', color: V.ink, border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 20px', fontSize: 13, fontWeight: 400, cursor: 'pointer', fontFamily: V.sans },
};
